import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {ConsumptionData, ConsumptionTable} from "../consumption-table/consumption-table";
import {ConsumptionChart} from '../consumption-chart/consumption-chart';
import {HouseholdService} from '../../services/household.service';
import {Subject, forkJoin, of} from 'rxjs';
import {catchError, distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {HeatConsumptionResponse} from '../../models/household-data';
import {mapToConsumptionDataDownsampled} from '../../mappers/map-to-consumption-data';
import {mapToHeatConsumptionResponse} from '../../mappers/household-data.mapper';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    ConsumptionTable,
    ConsumptionChart,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  houseData: HeatConsumptionResponse | null = null;
  startDate: Date = new Date();
  endDate: Date = new Date();
  public consumptionTableData: ConsumptionData[] = [];
  unauthorizedHouseId: string | null = null;

  private readonly _destroying$ = new Subject<void>();

  constructor(private householdService: HouseholdService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    const requestedHouseId = this.route.snapshot.queryParamMap.get('houseId');

    // Hausliste einmalig laden und im Service ablegen
    this.householdService.getHousesByUserId().subscribe({
      next: houses => {
        this.householdService.houses$.next(houses);
        this.householdService.loadAllHouseLabels(houses);
        if (requestedHouseId && !houses.includes(requestedHouseId)) {
          this.unauthorizedHouseId = requestedHouseId;
          return;
        }
        if (houses.length > 0 && !this.householdService.selectedHouseId$.value) {
          this.householdService.selectedHouseId$.next(requestedHouseId ?? houses[0]);
        }
      },
      error: err => console.error('[HomeComponent] Error loading houses:', err)
    });

    // Daten laden wenn sich das ausgewählte Haus ändert
    this.householdService.selectedHouseId$.pipe(
      filter(id => !!id),
      distinctUntilChanged(),
      takeUntil(this._destroying$)
    ).subscribe(houseId => {
      this.loadDataForHouse(houseId!);
    });
  }

  @Output() timeRangeChange = new EventEmitter<{ start: Date; end: Date }>();

  onTimeRangeChanged(event: { start: Date; end: Date }) {
    this.startDate = event.start;
    this.endDate = event.end;
    const houseId = this.householdService.selectedHouseId$.value;
    if (houseId) {
      this.loadDataForHouse(houseId);
    }
  }

  private loadDataForHouse(houseId: string): void {
    this.houseData = null;
    this.consumptionTableData = [];
    this.householdService.currentHouseholdData$.next(null);

    const prevStart = new Date(this.startDate);
    prevStart.setFullYear(prevStart.getFullYear() - 1);
    const prevEnd = new Date(this.endDate);
    prevEnd.setFullYear(prevEnd.getFullYear() - 1);

    forkJoin({
      household: this.householdService.getHouseholdConsumption(houseId, this.startDate, this.endDate),
      outdoorTemp: this.householdService.getEnvironmentTemperature(houseId, this.startDate, this.endDate).pipe(
        catchError(err => {
          console.warn('[HomeComponent] Außentemperatur nicht verfügbar:', err?.status ?? err);
          return of({ pointsFrequency: '', telemetry: [] });
        })
      ),
      prevYear: this.householdService.getHouseholdConsumption(houseId, prevStart, prevEnd).pipe(
        catchError(err => {
          console.warn('[HomeComponent] Vorjahresdaten nicht verfügbar:', err?.status ?? err);
          return of(null as HeatConsumptionResponse | null);
        })
      )
    }).subscribe({
      next: ({ household, outdoorTemp, prevYear }) => {
        this.houseData = mapToHeatConsumptionResponse(household);
        this.householdService.currentHouseholdData$.next(this.houseData);

        this.householdService.houseLabels$.next({
          ...this.householdService.houseLabels$.value,
          [houseId]: this.householdService.buildLabel(this.houseData)
        });

        const outdoorTelemetry = outdoorTemp.telemetry[0] ?? null;
        const currentData = mapToConsumptionDataDownsampled(
          this.houseData, this.startDate, this.endDate, outdoorTelemetry
        );

        if (prevYear) {
          const prevYearData = mapToConsumptionDataDownsampled(prevYear, prevStart, prevEnd);
          this.consumptionTableData = mergePrevYear(currentData, prevYearData);
        } else {
          this.consumptionTableData = currentData;
        }
      },
      error: err => console.error('[HomeComponent] Error loading data:', err)
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}

/**
 * Berechnet die Vorwärts-Differenzen der Zählerstände (wie Chart und Tabelle) aus dem
 * Vorjahresdatensatz und schreibt sie index-basiert als prev_year_consumption in die
 * aktuellen Zeilen. Beide Arrays sind bereits aufsteigend nach Datum sortiert.
 */
function mergePrevYear(current: ConsumptionData[], prevYear: ConsumptionData[]): ConsumptionData[] {
  const prevConsumption: (number | null)[] = prevYear.map((r, i, arr) => {
    if (i === arr.length - 1) return null;
    const curr = typeof r.meter_amount === 'number' ? r.meter_amount : null;
    const next = typeof arr[i + 1]?.meter_amount === 'number' ? arr[i + 1]!.meter_amount as number : null;
    if (curr === null || next === null) return null;
    const diff = next - curr;
    return diff >= 0 ? Math.round((diff + Number.EPSILON) * 10) / 10 : null;
  });

  return current.map((row, i) => ({
    ...row,
    prev_year_consumption: prevConsumption[i] ?? null
  }));
}
